import React from 'react';
import styled from 'styled-components';
import {
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { Send } from '@material-ui/icons';
import { TILES, getTileRates } from '../../utils/rates';
import { isResent } from '../../utils/graph';
import { formatDate, getCancelledText } from '../../utils/status';
import StatTile from './StatTile';
import StatusCell from './StatusCell';
import RowMenu from './RowMenu';
import EmergencyCancel from './EmergencyCancel';
import ShowSelectedToggle from './ShowSelectedToggle';
import { BLUE, GREY_TEXT, RESEND_COLOR } from '../../AnalyticsV2.styled';

export const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
// checkbox, message, the tiles, date, status, menu
const COLUMN_COUNT = TILES.length + 5;
// Wide enough for "Message" plus the "Show selected messages only" button, so
// the columns to the right do not move when the button appears.
const MESSAGE_COLUMN_WIDTH = 350;

// The header row stays at the top of the table while the rows scroll under
// it (Table stickyHeader); the white background hides the rows behind it.
const HeaderCell = styled(TableCell)`
  &.MuiTableCell-root {
    background: #ffffff;
    color: ${GREY_TEXT};
    font-size: 13px;
    white-space: nowrap;
    padding: 14px 4px;
  }
`;

const Cell = styled(TableCell)`
  &.MuiTableCell-root {
    padding: 22px 4px;
    vertical-align: middle;
  }
`;

// A message row followed by a line of its own (emergency cancel, or who
// cancelled it) has no border and a small bottom padding, so the two read as
// one row.
const MessageRow = styled(TableRow)`
  ${({ $hasLineBelow }) =>
    $hasLineBelow &&
    '& > .MuiTableCell-root { border-bottom: none; padding-bottom: 4px; }'}
`;

const LineBelowCell = styled(TableCell)`
  &.MuiTableCell-root {
    padding: 0 4px 16px;
  }
`;

const CancelledText = styled.span`
  font-size: 14px;
  color: ${GREY_TEXT};
`;

// Fills the card above the pagination, so the footer stays at the bottom.
const TableArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

// The rows scroll in here, under the sticky header row. The fetching overlay
// sits outside it, so it covers the visible rows wherever they are scrolled.
const TableScroll = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

// Covers rows already on screen while the next page, search or filter loads.
const FetchingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
`;

// Loading, error and empty states, centred in the free space under the header.
const Placeholder = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  color: ${GREY_TEXT};
`;

// "Message" and the "Show selected messages only" button, centred on one line
// so both texts line up.
const MessageHeader = styled.div`
  display: flex;
  align-items: center;
`;

// A click anywhere in the message cell opens the message's detail page.
const MessageCell = styled(Cell)`
  &.MuiTableCell-root {
    cursor: pointer;
  }
  &:hover button > div {
    color: ${BLUE};
  }
`;

// Gives the cell keyboard focus. Enter or Space on it fires a click that
// bubbles to the cell's onClick.
const MessageLink = styled.button`
  display: block;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
`;

const DraftName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #1c1c1c;
`;

// The A/B badge of the Drafts page (Card.AB in Drafts/Components/_CardUI).
const ABChip = styled.span`
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: bold;
  color: #61ac04;
  background-color: #f7ffe4;
`;

const ResendIcon = styled(Send)`
  &.MuiSvgIcon-root {
    margin-left: 8px;
    font-size: 16px;
    color: ${RESEND_COLOR};
    vertical-align: middle;
  }
`;

const Subject = styled.div`
  font-size: 13px;
  color: #1c1c1c;
  margin-top: 4px;
`;

const MessageTable = ({
  messages,
  isLoading,
  isFetching,
  isError,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  selected,
  maxSelected,
  onToggleRow,
  onTogglePage,
  showSelectedOnly,
  onShowSelectedOnlyChange,
  onTileClick,
  onOpenMessage,
  onChanged,
}) => {
  const selectedOnPage = messages.filter(message => selected.has(message.id));
  const isPageSelected =
    messages.length > 0 && selectedOnPage.length === messages.length;
  const isAtMax = selected.size >= maxSelected;
  // Ticking the header checkbox selects the page's other rows, which is only
  // allowed while the total stays at maxSelected or under. A fully selected
  // page can always be cleared.
  const canSelectPage =
    selected.size + messages.length - selectedOnPage.length <= maxSelected;
  const maxTooltip = `Up to ${maxSelected} messages can be selected at once`;

  const placeholder = isLoading ? (
    <CircularProgress style={{ color: BLUE }} />
  ) : isError ? (
    "Couldn't load messages. Please try again."
  ) : messages.length === 0 ? (
    'No messages found'
  ) : null;

  const renderBody = () => {
    return messages.map(message => {
      const rates = getTileRates(message);
      const isSending = message.status === 'sending';
      const isCancelled = message.status === 'cancelled';
      return (
        <React.Fragment key={message.id}>
          <MessageRow $hasLineBelow={isSending || isCancelled}>
            <Cell padding="checkbox">
              <Tooltip
                title={isAtMax && !selected.has(message.id) ? maxTooltip : ''}
                placement="top"
              >
                {/* A disabled checkbox gets no mouse events, so the tooltip
                    sits on this span. */}
                <span>
                  <Checkbox
                    color="primary"
                    checked={selected.has(message.id)}
                    disabled={isAtMax && !selected.has(message.id)}
                    onChange={() => onToggleRow(message)}
                  />
                </span>
              </Tooltip>
            </Cell>
            <MessageCell
              style={{ minWidth: MESSAGE_COLUMN_WIDTH }}
              onClick={() => onOpenMessage(message)}
            >
              <MessageLink>
                <DraftName>
                  {message.draftName}
                  {message.AB?.linkedMessageId && <ABChip>A/B</ABChip>}
                  {isResent(message) && (
                    <Tooltip title="Resent to non-openers" placement="top">
                      <ResendIcon aria-label="Resent to non-openers" />
                    </Tooltip>
                  )}
                </DraftName>
                <Subject>{message.messageSubject}</Subject>
              </MessageLink>
            </MessageCell>
            {TILES.map(({ key }) => (
              <Cell key={key}>
                <StatTile
                  tileKey={key}
                  rate={rates[key]}
                  onClick={() => onTileClick(message, key)}
                />
              </Cell>
            ))}
            <Cell style={{ paddingLeft: 24 }}>
              {formatDate(message.sendTime)}
            </Cell>
            <Cell>
              <StatusCell message={message} />
            </Cell>
            <Cell align="right">
              <RowMenu message={message} onChanged={onChanged} />
            </Cell>
          </MessageRow>
          {(isSending || isCancelled) && (
            <TableRow>
              {/* Right-aligned under the row, ending under the ... button. */}
              <LineBelowCell colSpan={COLUMN_COUNT} align="right">
                {isSending ? (
                  <EmergencyCancel message={message} onCancelled={onChanged} />
                ) : (
                  <CancelledText>{getCancelledText(message)}</CancelledText>
                )}
              </LineBelowCell>
            </TableRow>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <>
      <TableArea>
        <TableScroll>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <HeaderCell padding="checkbox">
                  <Tooltip
                    title={isPageSelected || canSelectPage ? '' : maxTooltip}
                    placement="top"
                  >
                    <span>
                      <Checkbox
                        color="primary"
                        checked={isPageSelected}
                        indeterminate={
                          selectedOnPage.length > 0 && !isPageSelected
                        }
                        disabled={
                          messages.length === 0 ||
                          (!isPageSelected && !canSelectPage)
                        }
                        onChange={() => onTogglePage(messages, isPageSelected)}
                      />
                    </span>
                  </Tooltip>
                </HeaderCell>
                <HeaderCell
                  style={{ fontSize: 15, minWidth: MESSAGE_COLUMN_WIDTH }}
                >
                  <MessageHeader>
                    Message
                    {selected.size > 0 && (
                      <ShowSelectedToggle
                        checked={showSelectedOnly}
                        onChange={onShowSelectedOnlyChange}
                      />
                    )}
                  </MessageHeader>
                </HeaderCell>
                {TILES.map(({ key, label }) => (
                  <HeaderCell key={key}>{label}</HeaderCell>
                ))}
                <HeaderCell style={{ paddingLeft: 24 }}>Date</HeaderCell>
                <HeaderCell>Status</HeaderCell>
                <HeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>{!placeholder && renderBody()}</TableBody>
          </Table>
          {placeholder && <Placeholder>{placeholder}</Placeholder>}
        </TableScroll>
        {isFetching && !isLoading && (
          <FetchingOverlay>
            <CircularProgress style={{ color: BLUE }} />
          </FetchingOverlay>
        )}
      </TableArea>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage="Row per page:"
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={event =>
          onRowsPerPageChange(Number(event.target.value))
        }
      />
    </>
  );
};

export default MessageTable;
